import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setUser } from "../features/auth/authSlice";
import config from "../../config";
import {
  apiUrl,
  endExpiredSession,
  readSharedAccessToken,
  shareAccessToken,
} from "../authSession";

const baseQuery = fetchBaseQuery({
  baseUrl: config.baseUrl,
  // "include" so the browser sends and accepts the httpOnly refresh cookie on
  // the cross-origin API (uniedu.fi → backend.uniedu.fi). The cookie itself is
  // scoped to /api/account/, so other endpoints never receive it.
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// ── Session renewal ─────────────────────────────────────────────────────────
// The refresh token is an httpOnly cookie that the backend rotates on every
// use and blacklists the old one (ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION), so each refresh token works exactly once and is
// shared by every tab. Two refreshes racing with the same cookie would make
// the loser look like an expired session. So:
//   - one refresh at a time per tab: every 401 waits on the same promise
//   - one refresh at a time across tabs: a Web Lock serialises them
//   - before spending the cookie, reuse an access token another request or
//     tab already obtained
//   - only a genuinely rejected refresh ends the session; network/server
//     failures keep the user signed in

const REFRESH_LOCK = "uniedu-token-refresh";

let refreshPromise = null;
// Access tokens the server has answered 401 to. Never reused or adopted.
const rejectedAccessTokens = new Set();

const storeAccessToken = (api, accessToken) => {
  const { user } = api.getState().auth;
  // refreshToken: null drops any token left in state by pre-cookie sessions.
  api.dispatch(setUser({ user, accessToken, refreshToken: null }));
};

const isUsable = (token) => Boolean(token) && !rejectedAccessTokens.has(token);

// Run `task` while holding a lock shared by all tabs of this site. Browsers
// without the Web Locks API just run it (the per-tab guard still applies).
const withCrossTabLock = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(REFRESH_LOCK, task)
    : task();

// Resolves to "refreshed" | "rejected" | "unavailable".
const runRefresh = async (api) => {
  // Renewed while we waited — by this tab, or another tab via shared storage.
  const current = api.getState().auth.accessToken;
  if (isUsable(current)) return "refreshed";
  const shared = readSharedAccessToken();
  if (isUsable(shared)) {
    storeAccessToken(api, shared);
    return "refreshed";
  }

  // Sessions from before the cookie keep their refresh token in state; send it
  // once so the backend can move them onto the cookie.
  const legacyRefresh = api.getState().auth.refreshToken;

  let res;
  try {
    res = await fetch(apiUrl("account/token/refresh/"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(legacyRefresh ? { refresh: legacyRefresh } : {}),
    });
  } catch {
    return "unavailable";
  }

  if (res.status === 401) return "rejected";
  if (!res.ok) return "unavailable"; // 403 origin, 429 throttled, 5xx…

  let data;
  try {
    data = await res.json();
  } catch {
    return "unavailable";
  }
  if (!data?.access) return "unavailable";

  // Share before releasing the lock so a waiting tab reuses it instead of
  // spending the (now rotated) cookie again.
  shareAccessToken(data.access);
  storeAccessToken(api, data.access);
  return "refreshed";
};

const renewSession = (api) => {
  if (!refreshPromise) {
    refreshPromise = withCrossTabLock(() => runRefresh(api)).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// An adopted token can itself turn out to be stale, so allow one more renewal
// (which then goes to the server) before giving up.
const MAX_RENEWALS = 2;

const baseQueryWithRefreshToken = async (args, api, extraOptions) => {
  let sentToken = api.getState().auth.accessToken;
  let result = await baseQuery(args, api, extraOptions);

  for (
    let attempt = 0;
    attempt < MAX_RENEWALS && result?.error?.status === 401;
    attempt += 1
  ) {
    // Anonymous request — there is no session to renew or end.
    if (!sentToken && !api.getState().auth.accessToken) return result;
    if (sentToken) rejectedAccessTokens.add(sentToken);

    const outcome = await renewSession(api);

    if (outcome === "rejected") {
      endExpiredSession(api);
      return result;
    }
    if (outcome === "unavailable") {
      // Couldn't reach the refresh endpoint. Keep the session and report a
      // connection problem rather than an auth failure.
      return {
        error: {
          status: "FETCH_ERROR",
          error: "Could not renew your session. Check your connection and try again.",
        },
      };
    }

    sentToken = api.getState().auth.accessToken;
    result = await baseQuery(args, api, extraOptions);
  }

  // Freshly issued tokens still rejected: the account itself is no longer
  // valid (deactivated, deleted).
  if (result?.error?.status === 401 && sentToken) {
    endExpiredSession(api);
  }

  // Note: per-status toasts are intentionally NOT raised here.
  // Components decide how to surface errors via `useApiFormErrors(setError)` so
  // field errors land on inputs (red border + message) and only non-field
  // errors become toasts. The only thing this layer handles globally is the
  // 401 → refresh → logout flow above.
  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithRefreshToken,
  endpoints: () => ({}),
  tagTypes: [
    "program",
    "publicProgram",
    "profile",
    "applicant",
    "university",
    "language",
    "country",
    "city",
    "experience",
    "eligibility",
    "eligibilityStats",
    "Partner",
    "State",
    "housing",
    "group",
    "permission",
    "student",
    "AdminUserData",
    "Agent",
    "journeyPhase",
    "journeySubPhase",
    "Agreement",
    "Subjects",
    "Faculties",
    "Certificate",
    "HousingImage",
    "Housing",
    "Experience",
    "comment",
    "studentNote",
    "PartnerOrg",
    "PartnerMember",
    "UniEduStaff",
    "PartnerUniversities",
    "PartnerPrograms",
    "UniversityProposal",
    "ProgramProposal",
    "PartnerPortfolio",
    "PartnerCommission",
    "knowledgeBase",
  ],
});
